import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import TenantContext from "../../../context/tenant/TenantContext";
import AuthContext from "../../../context/auth/AuthContext";
import LandLordContext from "../../../context/landlord/LandLordContext";
import "react-toastify/dist/ReactToastify.css";
import useTranslator from "../../../hooks/useTranslator";
import {
  isClientIdentificationActive,
  getUserDisplayName,
  haveValue,
  toast,
} from "../../../helper/common";
import { useCookies } from "react-cookie";
import ProfileTable from "../../../components/Profiles/ProfilesTable";
import AddAgentModal from "./AddAgentModal";
import socket from "../../../socket";
import ShareWorkspaceModal from "../../../components/Workspace/ShareWorkspaceModal";
import { WORKSPACE_ROLES_CAPABILITIES } from "../../../Utils/workspaceRoleCapabilities";
import DataLoadedError from "../../../components/ErrorModal/DataLoadedError";
import InviteTenantModal from "../../../components/InviteTenantModal";
import {
  Button,
  Icon,
  PageHeader,
  SkeletonTable,
  Tabs,
  TextField,
} from "../../../components/design";

const ProfileRequestNew = () => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const authContext = useContext(AuthContext);
  const {
    user,
    userType,
    loadMyNotifications,
    loadUserTenant,
    setErrorDataLoading,
    errorDataLoading,
  } = authContext;

  const tenantContext = useContext(TenantContext);
  const { shareProfile, checkForensicAnalysisStatus } = tenantContext;

  const landLordContext = useContext(LandLordContext);
  const {
    loadAgencyProfile,
    loadProfilesLandlord,
    properties,
    loadProperties,
    getTanentData,
    isCustomClientWorkspace,
    deaWorkspace,
  } = landLordContext;

  const [rentPercentage, setRentPercentage] = useState(
    user?.rent_percentage ?? 1
  );
  const [rentDoc, setRentDoc] = useState("current_annual_income");
  const [bulkShareModal, setBulkShareModal] = useState(false);

  const queryParameters = new URLSearchParams(window.location.search);
  const location = useLocation();

  //toggling between full and basic profile
  const [loading, setLoading] = useState(false);

  const [cookies, setCookie, removeCookie] = useCookies([
    "sharedProfileLink",
    "application_id",
  ]);
  const [effortRateModal, setEffortRateModal] = useState(false);
  const [currentWorkspace, setCurrentWorkspace] = useState("");

  const [filters, setFilters] = useState({
    createdAt: "desc",
    myScore: "",
  });

  const [profiles, setProfiles] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(15);
  const [total, setTotal] = useState(0);
  const [searchVal, setSearchVal] = useState(
    () => new URLSearchParams(window.location.search).get("search") ?? ""
  );
  const [tanentData, setTanentData] = useState([]);
  const loadingRef = useRef(false);
  const latestLoadRequestRef = useRef(0);
  const importInitiatedRef = useRef(false);
  const prevWorkspaceIdRef = useRef(null);
  const loadDataRef = useRef(null);
  const sentinelRef = useRef(null);
  const loadMoreRef = useRef({ page, profiles, total });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showInviteTenantModel, setShowInviteTenantModel] = useState(false);

  const [assignLLModal, setAssignLLModal] = useState({
    tenant_id: null,
    openModal: false,
  });
  const [tabSelected, setTabSelected] = useState("all");

  const [effortRatePecentage, setEffortRatePecentage] =
    useState(rentPercentage);

  useEffect(() => {
    let userid = localStorage.getItem("saveduserid");
    if (haveValue(user?._id) || haveValue(userid)) {
      useeffectfunc();
    }
    if (haveValue(userid)) {
      loadProperties(userid);
    }
  }, [filters]);

  useEffect(() => {
    fetchTanentData();
    if (haveValue(user?.rent_percentage)) {
      setRentPercentage(user?.rent_percentage);
      setEffortRatePecentage(user?.rent_percentage);
    }
    if (haveValue(user?.rent_from)) {
      setRentDoc(user?.rent_from);
    }
    if (haveValue(user?._id)) {
      loadProperties(user?._id);
    }
    const workspaceFromUrl = user?.workspaceIds?.filter(
      (val) => val?.uuid === workspaceId
    );
    setCurrentWorkspace(workspaceFromUrl?.[0]);
    isCustomClientWorkspace(user?.email);

    if (workspaceId !== prevWorkspaceIdRef.current) {
      setProfiles([]);
      setPage(1);
      setTotal(0);
      const params = new URLSearchParams(window.location.search);
      const searchFromUrl = params.get("search");
      if (searchFromUrl) {
        setSearchVal(searchFromUrl);
        params.delete("search");
        const newSearch = params.toString();
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname + (newSearch ? `?${newSearch}` : "")
        );
      } else {
        setSearchVal("");
      }
      setTabSelected("all");
      prevWorkspaceIdRef.current = workspaceId;
      loadingRef.current = false;
      if (!searchFromUrl) {
        loadData(1);
      }
    } else {
      useeffectfunc();
    }
  }, [user, workspaceId]);

  useEffect(() => {
    const handleForensicCompleted = () => {
      loadingRef.current = false;
      loadDataRef.current?.(1, searchValRef.current, tabSelectedRef.current);
    };
    socket.on("forensic_completed", handleForensicCompleted);

    return () => socket.off("forensic_completed", handleForensicCompleted);
  }, []);

  const useeffectfunc = async () => {
    const sharedProfileLink = queryParameters.get("sharedProfileLink");
    const navigateTo = queryParameters.get("navigateTo");
    if (haveValue(navigateTo)) {
      navigate(`/${navigateTo}`);
    }
    if (haveValue(sharedProfileLink)) {
      // await importProfileRedirect();
    } else {
      loadData();
    }
  };

  const fetchTanentData = async () => {
    try {
      const tanentData = await getTanentData();
      const dataArray = Array.isArray(tanentData?.data) ? tanentData.data : [];

      const updatedData = dataArray.map((item) => {
        const application = item?.application_id;
        if (application.participants.length > 0) {
          const participants = application.participants || [];
          return {
            label: item?.application_id?.title,
            share_profile_id: item?._id,
            value: item?.tenant_id?._id,
            sub_val: `with ${participants.length - 1} other tenants`,
            applicationId: item?.application_id?._id,
            application: item?.application_id,
            isApplication: true,
          };
        }
        return {
          label: getUserDisplayName(item?.tenant_id),
          share_profile_id: item?._id,
          value: item?.tenant_id?._id,
          sub_val: item?.tenant_id?.email,
        };
      });

      setTanentData(updatedData);
    } catch (error) {
      console.error("Error fetching tenant data:", error);
    }
  };

  const loadData = async (requestedPage = page, search = searchVal, filter) => {
    if (loadingRef.current && !search) return;
    const requestId = ++latestLoadRequestRef.current;
    loadingRef.current = true;

    if (requestedPage === 1) {
      setLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    try {
      let userid = localStorage.getItem("saveduserid");

      let response;

      // if (user?.isParentLL && haveValue(user?.agency_id)) {
      //   response = await loadAgencyProfile(
      //     user?.agency_id?._id,
      //     requestedPage,
      //     limit,
      //     search
      //   );
      // } else

      if (user?._id) {
        response = await loadProfilesLandlord(user?._id, "full", {
          searchVal: search,
          page: requestedPage,
          limit,
          filter: filter ?? tabSelected,
        });
      }

      if (!response) {
        return;
      }

      if (requestId !== latestLoadRequestRef.current) {
        return;
      }

      const { data, total: newTotal } = response;

      setTotal(newTotal);
      setPage(requestedPage);

      setProfiles((prev) => (requestedPage === 1 ? data : [...prev, ...data]));
      loadMyNotifications(userid, userType);
    } catch (err) {
      if (requestId === latestLoadRequestRef.current) {
        setErrorDataLoading(true);
      }
    } finally {
      if (requestId !== latestLoadRequestRef.current) {
        return;
      }
      setLoading(false);
      setIsLoadingMore(false);
      loadingRef.current = false;

      setTimeout(() => {
        if (
          document.body.offsetHeight < window.innerHeight &&
          loadMoreRef.current.profiles.length < loadMoreRef.current.total &&
          !loadingRef.current
        ) {
          loadDataRef.current(loadMoreRef.current.page + 1);
        }
      }, 100);
    }
  };

  loadDataRef.current = loadData;

  // Keep latest search/tab in refs so socket + polling callbacks never go stale.
  const searchValRef = useRef(searchVal);
  searchValRef.current = searchVal;
  const tabSelectedRef = useRef(tabSelected);
  tabSelectedRef.current = tabSelected;
  const checkForensicStatusRef = useRef(null);
  checkForensicStatusRef.current = checkForensicAnalysisStatus;

  // Poll forensic status while any visible row shows the processing tag.
  // Mirrors skorList.jsx: 30s interval, stop on COMPLETED (reload list once),
  // timeout, or consecutive errors. Socket stays as the fast path.
  const FORENSIC_POLL_INTERVAL_MS = 30000;
  const FORENSIC_MAX_POLL_ATTEMPTS = 80;
  const FORENSIC_MAX_CONSECUTIVE_ERRORS = 8;
  const forensicPollTimeoutRef = useRef(null);
  const forensicLastReloadRef = useRef({ key: "", at: 0 });

  useEffect(() => {
    const ids = new Set();
    (profiles ?? []).forEach((profile) => {
      if (profile?.deleted) return;
      if (profile?.pendingDecision) return;
      const participants = profile?.application_id?.participants;
      if (Array.isArray(participants) && participants.length > 0) {
        participants.forEach((p) => {
          if (p?.deleted || p?.is_deleted) return;
          if (p?.consent_status && p.consent_status !== "accepted") return;
          const tid = p?.tenant_info?._id ?? p?.tenant_info;
          if (!haveValue(tid)) return;
          if (p?.tenant_info?.forensic_status === "COMPLETED") return;
          ids.add(tid?.toString?.() ?? tid);
        });
        return;
      }
      const tenantId = profile?.tenantid?._id;
      if (!haveValue(tenantId)) return;
      const notified =
        profile?.tenantid?.tenant_extra_field_id?.landlord_ids_for_forensic_notification?.some(
          (i) => i?.profile_id === profile?._id
        ) === true;
      const forensicPending =
        profile?.tenantid?.forensic_status !== "COMPLETED";
      if (notified || forensicPending) ids.add(tenantId.toString());
    });
    const processingIds = [...ids];
    const pollKey = processingIds.sort().join("|");

    if (processingIds.length === 0) {
      forensicLastReloadRef.current = { key: "", at: 0 };
      if (forensicPollTimeoutRef.current) {
        clearTimeout(forensicPollTimeoutRef.current);
        forensicPollTimeoutRef.current = null;
      }
      return undefined;
    }
    if (typeof checkForensicStatusRef.current !== "function") return undefined;

    let cancelled = false;
    let attempts = 0;
    let consecutiveErrors = 0;

    const stopPolling = () => {
      if (forensicPollTimeoutRef.current) {
        clearTimeout(forensicPollTimeoutRef.current);
        forensicPollTimeoutRef.current = null;
      }
    };

    const scheduleNext = () => {
      if (cancelled) return;
      forensicPollTimeoutRef.current = setTimeout(
        pollOnce,
        FORENSIC_POLL_INTERVAL_MS
      );
    };

    const pollOnce = async () => {
      if (cancelled) return;
      attempts += 1;
      let results = [];
      try {
        results = await Promise.allSettled(
          processingIds.map((id) => checkForensicStatusRef.current(id))
        );
      } catch {
        results = [];
      }
      if (cancelled) return;
      const successfulResults = results.filter(
        (r) => r.status === "fulfilled" && r.value?.success
      );
      const anyCompleted = successfulResults.some(
        (r) =>
          String(r.value?.success_res?.forensic_status ?? "").toUpperCase() ===
          "COMPLETED"
      );
      if (anyCompleted) {
        const now = Date.now();
        const lastReload = forensicLastReloadRef.current;
        const reloadCooldownElapsed =
          lastReload.key !== pollKey ||
          now - lastReload.at >= FORENSIC_POLL_INTERVAL_MS;

        if (!reloadCooldownElapsed) {
          scheduleNext();
          return;
        }

        forensicLastReloadRef.current = { key: pollKey, at: now };
        stopPolling();
        try {
          loadingRef.current = false;
          await loadDataRef.current?.(
            1,
            searchValRef.current,
            tabSelectedRef.current
          );
        } catch {
          // A later poll cycle or socket event can retry the refresh.
        }
        return;
      }

      const anyError =
        results.length !== processingIds.length ||
        results.some((r) => r.status !== "fulfilled" || !r.value?.success);
      if (anyError) {
        consecutiveErrors += 1;
        if (
          consecutiveErrors >= FORENSIC_MAX_CONSECUTIVE_ERRORS ||
          attempts >= FORENSIC_MAX_POLL_ATTEMPTS
        ) {
          stopPolling();
          return;
        }
        scheduleNext();
        return;
      }
      consecutiveErrors = 0;
      if (attempts >= FORENSIC_MAX_POLL_ATTEMPTS) {
        stopPolling();
        return;
      }
      scheduleNext();
    };

    pollOnce();

    return () => {
      cancelled = true;
      stopPolling();
    };
    // eslint-disable-next-line
  }, [profiles]);

  useEffect(() => {
    const handler = () => {
      loadingRef.current = false;
      loadDataRef.current(1);
    };
    window.addEventListener("profiles-refresh", handler);
    return () => window.removeEventListener("profiles-refresh", handler);
  }, []);

  const refreshWorkspaceFunc = () => {
    loadData();
    fetchTanentData();
  };

  useEffect(() => {
    importProfileRedirect();
  }, [user]);

  const importProfileRedirect = async () => {
    if (importInitiatedRef.current) return;

    let userid = localStorage.getItem("saveduserid");
    const sharedProfileLink = cookies.sharedProfileLink;
    const application_id = cookies.application_id;

    if (haveValue(sharedProfileLink)) {
      importInitiatedRef.current = true;
      const shareProfileResponse = await shareProfile(
        sharedProfileLink,
        userid,
        "null",
        "full",
        {
          shareType: "tLink",
          ...(application_id && { application_id }),
        }
      );
      removeCookie("sharedProfileLink", { maxAge: 1800 });
      if (application_id) {
        removeCookie("application_id", { maxAge: 1800 });
      }

      if (shareProfileResponse?.success) {
        toast.success(_t("profileImported"));
        if (application_id) {
          return;
        }
      } else if (shareProfileResponse?.key === "profile-not-found") {
        toast.error(shareProfileResponse?.errormsg ?? _t("failedToGetProfile"));
        return;
      } else if (shareProfileResponse?.key === "access-cancelled") {
        toast.error(
          _t(shareProfileResponse?.errormsg) ?? _t("failedToGetProfile")
        );
        return;
      } else {
        toast.error(shareProfileResponse?.errormsg ?? _t("failedToGetProfile"));
        return;
      }
      navigate(
        `/${workspaceId}/profile-property?sharedprofileid=${shareProfileResponse?.sharedProfileId}`,
        {
          state: { from: location.pathname + location.search },
        }
      );
    }
  };

  const searchTenant = (e) => {
    const q = e.target.value;
    setSearchVal(q);
    setPage(1);
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setProfiles([]);
      loadData(1, searchVal, tabSelected);
    }, 200);

    return () => clearTimeout(delayDebounceFn);
  }, [searchVal, tabSelected]);

  const checkSalary = (user) => {
    if (haveValue(user?.gurantor_id) && haveValue(user?.gurantor_id?._id)) {
      if (user?.gurantor_id?.algoanIncome) {
        let monthlyIncome = parseInt(
          user?.gurantor_id?.algoanIncome?.monthlyAmount
        );

        let userIncome = parseInt(
          haveValue(user?.gurantor_id?.current_annual_income)
            ? user?.gurantor_id?.current_annual_income
            : 0
        );
        let salaryMatch = Math.round((monthlyIncome / userIncome) * 100);

        return {
          matched: salaryMatch,
          salary: monthlyIncome,
        };
      }
    } else {
      if (user?.algoanIncome) {
        let monthlyIncome = parseInt(user?.algoanIncome?.monthlyAmount);

        let userIncome = parseInt(
          haveValue(user.current_annual_income) ? user.current_annual_income : 0
        );
        let salaryMatch = Math.round((monthlyIncome / userIncome) * 100);
        return {
          matched: salaryMatch,
          salary: monthlyIncome,
        };
      }
    }
  };

  const openAssignLLModal = (tenant_id) => {
    setAssignLLModal((prev) => ({
      ...prev,
      tenant_id: tenant_id,
      openModal: true,
    }));
  };
  const handleCloseModal = () => {
    loadData();
    setAssignLLModal((prev) => ({
      ...prev,
      tenant_id: null,
      openModal: false,
    }));
  };

  useEffect(() => {
    loadMoreRef.current = { page, profiles, total };
  }, [page, profiles, total]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const {
            page: currentPage,
            profiles: currentProfiles,
            total: currentTotal,
          } = loadMoreRef.current;
          if (!loadingRef.current && currentProfiles.length < currentTotal) {
            loadDataRef.current(currentPage + 1);
          }
        }
      },
      { rootMargin: "300px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [profiles.length]);

  return (
    <>
      <div
        className="main-page-wrapper mb-4 position-relative profileRequestWrapper"
        onClick={() => setEffortRateModal(false)}
      >
        <PageHeader
          title={_t("tenants")}
          actions={
            <>
              {userType === "landlord" &&
                WORKSPACE_ROLES_CAPABILITIES?.tenants?.inviteTenant?.includes(
                  user?.current_workspace_role
                ) && (
                  <Button
                    variant="accent"
                    iconStart={
                      <img src="/images/invite-tenant-icon.svg" alt="" />
                    }
                    onClick={() => setShowInviteTenantModel(true)}
                  >
                    {_t("requestProfile")}
                  </Button>
                )}
              {WORKSPACE_ROLES_CAPABILITIES?.tenants?.share?.includes(
                user?.current_workspace_role
              ) &&
                user?.current_workspace?._id?.toString() ===
                  currentWorkspace?._id?.toString() && (
                  <Button
                    className="tutorial-tenant-share-button"
                    onClick={() => setBulkShareModal(true)}
                  >
                    {_t("bulkShare")}
                  </Button>
                )}
            </>
          }
        />
        <div className="">
          {errorDataLoading ? (
            <DataLoadedError />
          ) : (
            <div className="profile-table-card">
              <div className="profile-table-header">
                <div className="profile-table-toolbar">
                  <Tabs
                    value={tabSelected}
                    onChange={setTabSelected}
                    items={["all", "unassigned", "assigned", "renting"].map(
                      (btnText) => ({
                        value: btnText,
                        label: _t(btnText),
                      })
                    )}
                  />
                  <TextField
                    id="tenant-search"
                    type="search"
                    size="sm"
                    className="profile-table-search"
                    icon={<Icon name="search" size={18} />}
                    onChange={searchTenant}
                    value={searchVal}
                    placeholder={_t("search")}
                    aria-label={_t("search")}
                  />
                </div>
              </div>
              <div className="tabs-item d-block p-0">
                {loading ? (
                  <SkeletonTable rows={8} columns={7} label={_t("loading")} />
                ) : (
                  <>
                    <ProfileTable
                      profile={profiles}
                      properties={properties}
                      loadData={loadData}
                      user={user}
                      openAssignLLModal={openAssignLLModal}
                      checkSalary={checkSalary}
                      rentPercentage={rentPercentage}
                      rentDoc={rentDoc}
                      deaWorkspace={deaWorkspace}
                      clientIdentificationRule={isClientIdentificationActive(
                        currentWorkspace
                      )}
                    />
                    {isLoadingMore ? (
                      <SkeletonTable
                        rows={2}
                        columns={7}
                        label={_t("loading")}
                      />
                    ) : (
                      profiles?.length === 0 && (
                        <div className="defaultSkorLoaderParent mt-3">
                          {_t(
                            "noProfilesFound",
                            "Used text if there is no profile in landlord's tenant profile"
                          )}
                        </div>
                      )
                    )}
                  </>
                )}
                <div ref={sentinelRef} style={{ height: 1 }} />
              </div>
            </div>
          )}
        </div>
      </div>
      {assignLLModal.openModal && (
        <AddAgentModal
          tenant_id={assignLLModal.tenant_id}
          openModal={assignLLModal.openModal}
          handleCloseModal={handleCloseModal}
        />
      )}

      {bulkShareModal && (
        <ShareWorkspaceModal
          isOpen={bulkShareModal}
          onClose={() => setBulkShareModal(false)}
          wholeData={tanentData}
          dataType={"tenants"}
          refreshFunc={refreshWorkspaceFunc}
          isFromTenantProfile
        />
      )}
      <InviteTenantModal
        open={showInviteTenantModel}
        onClose={() => setShowInviteTenantModel(false)}
      />
    </>
  );
};

export default ProfileRequestNew;
